import { Link } from "@tanstack/react-router";
import { calendarDatePath } from "@/lib/calendar/dates";
import {
  hijriToGregorian,
  daysInHijriMonth,
  HIJRI_MONTHS,
  HIJRI_MONTHS_EN,
} from "@/lib/calendar/hijri";
import { getHijriEventsForMonth } from "@/lib/calendar/hijri-events";
import { getHolidaysForDate } from "@/data/holidays";
import { useLocale } from "@/i18n/LocaleContext";
import { translateHolidayName } from "@/i18n/holidays";

interface Props {
  hYear: number;
  hMonth: number;
}

/**
 * Compact list under each Hijri mini month: Islamic events for that
 * month + Indonesian holidays falling within that Hijri month.
 */
export function HijriMiniMonthLegend({ hYear, hMonth }: Props) {
  const { t, href, locale } = useLocale();
  const monthName = (locale === "en" ? HIJRI_MONTHS_EN : HIJRI_MONTHS)[hMonth - 1];
  const events = getHijriEventsForMonth(hMonth);

  const total = daysInHijriMonth(hYear, hMonth);
  const liburRows: Array<{ hDay: number; gY: number; gM: number; gD: number; name: string; type: "libur" | "cuti-bersama" }> = [];
  for (let d = 1; d <= total; d++) {
    const g = hijriToGregorian(hYear, hMonth, d);
    const iso = `${g.y}-${String(g.m).padStart(2, "0")}-${String(g.d).padStart(2, "0")}`;
    for (const h of getHolidaysForDate(iso)) {
      liburRows.push({ hDay: d, gY: g.y, gM: g.m, gD: g.d, name: h.name, type: h.type });
    }
  }

  if (events.length === 0 && liburRows.length === 0) return null;

  return (
    <ul className="mt-2 space-y-1 rounded-md border border-border/60 bg-muted/30 px-2.5 py-2">
      {events.map((ev) => {
        const g = hijriToGregorian(hYear, hMonth, ev.hDay);
        const name = locale === "en" ? ev.nameEn : ev.name;
        const dotClass = ev.type === "hari-besar"
          ? "bg-emerald-500"
          : ev.type === "puasa" ? "bg-amber-500" : "bg-muted-foreground";
        return (
          <li key={`e-${ev.hDay}-${ev.name}`} className="flex items-start gap-1.5 text-xs leading-snug">
            <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
            <Link to={href(calendarDatePath(g.y, g.m, g.d)) as never} className="hover:text-primary">
              <span className="font-medium">{ev.hDay} {monthName}</span>
              <span className="text-muted-foreground"> ({g.d} {t.common.months[g.m - 1]})</span>
              <span className="text-muted-foreground"> - </span>
              <span>{name}</span>
            </Link>
          </li>
        );
      })}
      {liburRows.map((h, i) => {
        const isLibur = h.type === "libur";
        const dotClass = isLibur ? "bg-red-date" : "bg-cuti-bersama";
        const txtClass = isLibur ? "text-red-date font-medium" : "text-cuti-bersama font-medium";
        return (
          <li key={`h-${i}`} className="flex items-start gap-1.5 text-xs leading-snug">
            <span className={`mt-1 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass}`} />
            <Link to={href(calendarDatePath(h.gY, h.gM, h.gD)) as never} className="hover:text-primary">
              <span className={txtClass}>{h.hDay} {monthName}</span>
              <span className="text-muted-foreground"> ({h.gD} {t.common.months[h.gM - 1]})</span>
              <span className="text-muted-foreground"> - </span>
              <span>{translateHolidayName(h.name, locale)}</span>
            </Link>
          </li>
        );
      })}
    </ul>
  );
}